/**
 * The one place a plant is read against a place.
 *
 * ## Why this has to be a single service
 *
 * The failure mode this module exists to prevent is not a wrong answer — it is *several* answers. The
 * moment the palette computes its own suitability, the renderer its own leaf-out day and the ecology
 * report its own fruiting date, they begin to disagree, and every disagreement is invisible: each
 * screen is internally consistent and the site as a whole is incoherent. A tile says a shrub is
 * marginal, the canvas draws it in full leaf in March, the wildlife calendar puts its berries in a
 * month the renderer never shows fruit.
 *
 * So there is one resolver, and everything that needs a localized fact asks it:
 *
 * ```text
 *                       ┌─ Rendering (getPlantState)
 *                       ├─ Seasonal slider
 *  Flora ──┐            ├─ Ecology reports
 *          ├─ Resolver ─┼─ Wildlife scoring
 *  Site ───┤            ├─ Suitability badges
 *          │            ├─ Recommendations
 *  Plant ──┘            └─ Search and filtering
 * ```
 *
 * ## Nothing here is stored
 *
 * {@link ResolvedFlora} is computed, cached, and thrown away. The cache is keyed by
 * `floraId + localization + catalog version + overrides` and is regenerable from canonical data at
 * any moment — dropping it costs microseconds and can never cost information. That is the guarantee
 * that makes "no per-zone flora records" enforceable rather than merely intended: there is nowhere to
 * put one.
 *
 * Re-localizing a site is therefore a cache clear and a redraw. No flora record is touched, no
 * project field is rewritten, and the previous profile leaves nothing behind.
 */

import type { FloraDefinition } from '@/flora/FloraDefinition';
import type { FloraInstance, PlantSiteOverrides } from '@/flora/FloraInstance';
import { resolveSeasonalAppearance, type SeasonalRenderState } from '@/flora/FloraSeasonModel';
import { resolveEcology, type ResolvedEcology } from './Ecology';
import { resolvePhenology, type ResolvedPhenology } from './Phenology';
import {
  assessSuitability,
  type NativeStatus,
  type SuitabilityAssessment,
  type SuitabilityRating,
} from './Suitability';
import {
  defaultLocalization,
  localizationKey,
  type SiteLocalization,
} from './SiteLocalization';

/**
 * The mature size this plant is expected to reach *here*.
 *
 * A record's `matureHeightMeters` is a range for a reason: the same species tops out lower at the
 * cold edge of its range, where it spends part of every year recovering rather than growing, and
 * lower again where summers cook it. This picks a point in the range the record already carries
 * rather than inventing a number, which keeps the answer inside what the botany claims.
 */
export interface ResolvedGrowth {
  matureHeightMeters: number;
  matureSpreadMeters: number;
  /** Where in the record's own range the site puts this plant, 0 at minimum, 1 at maximum. */
  vigor: number;
  /** True when pruning or a growth override, not the climate, decided the figure. */
  managed: boolean;
}

export interface ResolvedFlora {
  floraId: string;
  /** The canonical record, unchanged. Held so consumers never need a second lookup. */
  definition: FloraDefinition;
  suitability: SuitabilityAssessment;
  phenology: ResolvedPhenology;
  ecology: ResolvedEcology;
  growth: ResolvedGrowth;
  nativeStatus: NativeStatus;
  /** Convenience mirror of `suitability.rating`, which is what most call sites want. */
  rating: SuitabilityRating;
}

/** A stable key for an override block, so two identically-overridden plants share a cache entry. */
function overrideKey(overrides: PlantSiteOverrides | undefined): string {
  if (!overrides) return '-';
  return [
    overrides.irrigated === true ? 'i' : '',
    overrides.protectedMicroclimate === true ? 'p' : '',
    overrides.containerPlanted === true ? 'c' : '',
    overrides.managedHeightMeters ?? '',
    overrides.managedSpreadMeters ?? '',
    overrides.growthScale ?? '',
  ].join(',');
}

/**
 * Hardiness the overrides add or take away, in zone values.
 *
 * A pot loses more than a wall gains, and that asymmetry is real: a sheltered position moderates the
 * air a plant stands in, while a container exposes its roots to that air directly, which is the part
 * of a plant least able to survive freezing.
 */
function zoneAdjustmentOf(overrides: PlantSiteOverrides | undefined): number {
  let adjustment = 0;
  if (overrides?.protectedMicroclimate === true) adjustment += 0.5;
  if (overrides?.containerPlanted === true) adjustment -= 1;
  return adjustment;
}

/**
 * Where in its size range the site puts a plant.
 *
 * Cold headroom drives it: a plant one zone inside its limit reaches the low end of its range, three
 * zones inside reaches the typical figure, and only where it is comfortable at both ends does it push
 * toward the maximum. Deliberately gentle — this is a plausible reading of a known effect, not a
 * measurement, and a model that shrank plants dramatically would be making claims the data cannot
 * support.
 */
function resolveGrowth(
  definition: FloraDefinition,
  suitability: SuitabilityAssessment,
  overrides: PlantSiteOverrides | undefined,
): ResolvedGrowth {
  const height = definition.growth.matureHeightMeters;
  const spread = definition.growth.matureSpreadMeters;

  const headroom = Number.isFinite(suitability.coldHeadroom) ? suitability.coldHeadroom : 3;
  // 0 at the plant's cold limit, 1 three zones inside it.
  let vigor = clamp01(headroom / 3);
  if (suitability.heatHeadroom !== null && suitability.heatHeadroom < 1) {
    // Heat stress pulls a plant back down its own range from the other direction.
    vigor = Math.min(vigor, clamp01((suitability.heatHeadroom + 1) / 2));
  }
  if (!suitability.localized) vigor = typicalPosition(height.minimum, height.typical, height.maximum);

  const scale = overrides?.growthScale ?? 1;
  const managedHeight = overrides?.managedHeightMeters;
  const managedSpread = overrides?.managedSpreadMeters;

  return {
    matureHeightMeters:
      managedHeight ?? lerpRange(height.minimum, height.typical, height.maximum, vigor) * scale,
    matureSpreadMeters:
      managedSpread ?? lerpRange(spread.minimum, spread.typical, spread.maximum, vigor) * scale,
    vigor,
    managed: managedHeight !== undefined || managedSpread !== undefined || scale !== 1,
  };
}

/**
 * Walk a `minimum → typical → maximum` range by a 0–1 parameter, passing through `typical` at 0.5.
 *
 * Two straight segments rather than one, because `typical` is not usually the midpoint and a linear
 * interpolation between the extremes would quietly report a size the record never claimed as typical.
 */
function lerpRange(minimum: number, typical: number, maximum: number, t: number): number {
  const clamped = clamp01(t);
  return clamped <= 0.5
    ? minimum + (typical - minimum) * (clamped / 0.5)
    : typical + (maximum - typical) * ((clamped - 0.5) / 0.5);
}

/** The parameter at which {@link lerpRange} returns `typical` — used for the unlocalized case. */
function typicalPosition(minimum: number, typical: number, maximum: number): number {
  void minimum;
  void typical;
  void maximum;
  return 0.5;
}

export interface FloraResolverOptions {
  /** The site's profile. Read through a function so the resolver never holds a stale copy. */
  localization: () => SiteLocalization;
  /**
   * Catalog version, which is the third leg of the cache key. Bumping the catalog invalidates every
   * entry, which is what makes the cache safe to keep across a data reload.
   */
  catalogVersion?: () => string;
}

export class FloraResolver {
  private readonly cache = new Map<string, ResolvedFlora>();
  /** The key the cache was filled against; a change to it empties the cache rather than poisoning it. */
  private cacheProfile = '';

  constructor(private readonly options: FloraResolverOptions) {}

  get localization(): SiteLocalization {
    return this.options.localization();
  }

  /**
   * Resolve a plant for this site.
   *
   * `overrides` comes from a planted specimen; omit it to resolve the species in the abstract, which
   * is what the palette and the search filters want.
   */
  resolve(definition: FloraDefinition, overrides?: PlantSiteOverrides): ResolvedFlora {
    const localization = this.localization;
    const profile = `${localizationKey(localization)}|${this.options.catalogVersion?.() ?? '0'}`;
    if (profile !== this.cacheProfile) {
      this.cache.clear();
      this.cacheProfile = profile;
    }

    const key = `${definition.id}|${overrideKey(overrides)}`;
    const hit = this.cache.get(key);
    if (hit) return hit;

    const suitability = assessSuitability(definition, localization, {
      zoneAdjustment: zoneAdjustmentOf(overrides),
      ...(overrides?.irrigated === true ? { irrigated: true } : {}),
    });
    const phenology = resolvePhenology(definition, localization);

    const resolved: ResolvedFlora = {
      floraId: definition.id,
      definition,
      suitability,
      phenology,
      ecology: resolveEcology(definition, phenology),
      growth: resolveGrowth(definition, suitability, overrides),
      nativeStatus: suitability.nativeStatus,
      rating: suitability.rating,
    };
    this.cache.set(key, resolved);
    return resolved;
  }

  /** Resolve for a planted specimen, reading its overrides off the instance. */
  resolveInstance(definition: FloraDefinition, instance: FloraInstance): ResolvedFlora {
    return this.resolve(definition, instance.siteOverrides);
  }

  /**
   * What a plant looks like on a given day, here.
   *
   * The renderer's entry point, and the reason the renderer contains no geography: it hands over a
   * definition, a date and a specimen, and receives the same {@link SeasonalRenderState} it has always
   * consumed. What changed is that the days behind that state came from the site.
   *
   * Two shifts meet here and they are deliberately kept apart. The **site** moves the plant's own
   * events — its leaf-out, its bloom — by rewriting the season definition, so the ambient season
   * stays where the calendar says it is and only the plant moves. The specimen's
   * `seasonalOffsetDays` is a different thing: a few days of variation so a hedge of one clone does
   * not turn colour in lockstep, applied at evaluation time exactly as it always was. Folding the
   * second into the first would look equivalent and is not — it would drag the seasonal blend along
   * with it, and reverse the sign of an offset that has always meant "this plant runs ahead".
   *
   * A specimen with `seasonOverrideDayOfYear` is pinned to a fixed date and ignores both the global
   * season and its own offset — that is what "pin this plant to June" means, and it was true before
   * localization existed.
   */
  getPlantState(
    definition: FloraDefinition,
    dayOfYear: number,
    instance?: FloraInstance,
  ): SeasonalRenderState {
    const resolved = this.resolve(definition, instance?.siteOverrides);
    if (instance?.seasonOverrideDayOfYear !== undefined) {
      return resolveSeasonalAppearance(
        resolved.phenology.seasons,
        instance.seasonOverrideDayOfYear,
        0,
      );
    }
    return resolveSeasonalAppearance(
      resolved.phenology.seasons,
      dayOfYear,
      instance?.seasonalOffsetDays ?? 0,
    );
  }

  /**
   * Drop every derived value.
   *
   * Called when the site is localized. Safe at any moment and from anywhere: the cache holds nothing
   * that is not recomputable from the canonical record and the current profile.
   */
  invalidate(): void {
    this.cache.clear();
    this.cacheProfile = '';
  }

  /** Entries currently held, for the diagnostics readout. */
  get cacheSize(): number {
    return this.cache.size;
  }
}

/**
 * A resolver for a site nobody has localized.
 *
 * Used by tests and by any consumer that needs a resolver before the application has one. It resolves
 * against the reference climate, which reproduces the app's pre-localization behaviour exactly.
 */
export function unlocalizedResolver(): FloraResolver {
  const localization = defaultLocalization();
  return new FloraResolver({ localization: () => localization });
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}
